'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import {
  fetchCustomScenarios,
  insertCustomScenario,
  CustomScenario,
} from '@/lib/services/customScenarioService';
import { saveDailyResponse } from '@/lib/services/dailyResponseService';
import { createClient } from '@/lib/supabase/client';

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
interface Scenario {
  id: number;
  context: string;
  icon: string;
  reflection: string[];
  example: string;
  emotionalResponse: string;
  insight: string;
  action: string;
}

const scenarios: Scenario[] = [
  {
    id: 1,
    context: 'Team Work (Recognition)',
    icon: 'UsersIcon',
    reflection: [
      'What emotions dominate my day?',
      'When do I feel most unappreciated at work?',
    ],
    example: 'Feeling irritated during team meetings when your contributions are overlooked',
    emotionalResponse: 'Irritation, frustration, resentment',
    insight:
      'Irritation is often linked to feeling unheard or undervalued. Recognizing this helps you separate the emotion from the person.',
    action:
      'Write in your journal: "I feel frustrated when my ideas are ignored because I value being heard and respected."',
  },
  {
    id: 2,
    context: 'Love Affairs (Romantic Relationship)',
    icon: 'HeartIcon',
    reflection: [
      'What emotions do I feel toward my partner today?',
      'What situation recently disturbed my emotional balance?',
      'Am I reacting to the present or to a past wound?',
    ],
    example: 'Partner delayed responding to messages or appeared distant and unavailable',
    emotionalResponse: 'Anxiety, insecurity, overthinking, fear of abandonment',
    insight:
      'Fear of rejection and the need for reassurance are deeply human. Naming this emotion reduces its power over your reactions.',
    action:
      'Write in your journal: "I feel anxious when my partner is distant because I value closeness and security. This is about my need, not their intent."',
  },
  {
    id: 3,
    context: 'Husband & Wife (Marriage)',
    icon: 'HomeIcon',
    reflection: [
      'What recurring emotional tension exists in my marriage?',
      'When do I feel most misunderstood by my spouse?',
      'What do I wish my partner understood about me?',
    ],
    example: 'Spouse dismisses your opinion during household decision-making or financial planning',
    emotionalResponse: 'Frustration, feeling disrespected, suppressed anger, withdrawal',
    insight:
      'The emotional need for recognition and mutual respect is foundational in marriage. Unmet, it builds invisible walls between partners.',
    action:
      'Write in your journal: "I feel frustrated when my input is overlooked because I value mutual respect and inclusion in our shared life."',
  },
  {
    id: 4,
    context: 'Bad Boss (Workplace Conflict)',
    icon: 'BriefcaseIcon',
    reflection: [
      'What emotions arise when I think about my supervisor?',
      'What specific behavior triggers these emotions?',
      'How does this affect my performance and self-worth?',
    ],
    example: 'Boss criticizes your work harshly in front of colleagues during a team meeting',
    emotionalResponse: 'Embarrassment, anger, demotivation, self-doubt',
    insight:
      'Sensitivity to public criticism reflects a desire for professional dignity. This is not weakness — it is a healthy need for respectful leadership.',
    action:
      'Write in your journal: "I feel demotivated when criticized publicly because I value respect and constructive feedback. I deserve to be corrected with dignity."',
  },
  {
    id: 5,
    context: 'No Employment (Unemployment)',
    icon: 'ExclamationCircleIcon',
    reflection: [
      'What emotions dominate my current situation?',
      'What thoughts repeatedly come to mind about my future?',
      'How am I treating myself during this difficult season?',
    ],
    example: 'Experiencing repeated job rejections or prolonged lack of employment opportunities',
    emotionalResponse: 'Stress, self-doubt, hopelessness, shame, isolation',
    insight:
      'Fear of uncertainty and a perceived lack of progress are natural responses to unemployment. Your worth is not defined by your employment status.',
    action:
      'Write in your journal: "I feel anxious about my future due to uncertainty, not because I lack ability. I am capable, and this season is temporary."',
  },
];

interface Ritual {
  time: string;
  title: string;
  duration: string;
  description: string;
  steps: string[];
  color: string;
  bgColor: string;
  borderColor: string;
  iconBg: string;
  emoji: string;
}

const rituals: Ritual[] = [
  {
    time: 'Morning',
    title: 'Alignment + Intention',
    duration: '5–7 min',
    description:
      'Begin your day by centering yourself and setting a conscious intention for how you want to show up in the world.',
    steps: [
      'Take 3 deep breaths to arrive fully in the present moment',
      'Ask yourself: "Who do I choose to be today?"',
      'Set one clear intention — a quality, not a task (e.g., patience, openness, kindness)',
      'Visualize yourself embodying that quality throughout the day',
    ],
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-100',
    emoji: '🌅',
  },
  {
    time: 'Daytime',
    title: 'Conscious Practice',
    duration: '5–7 min',
    description:
      'Pause during your active hours to reconnect with your intention and practice conscious awareness in real-time.',
    steps: [
      'Step away from your current activity for a brief pause',
      'Check in: "Am I aligned with my morning intention right now?"',
      'Notice any tension, reaction, or emotion without judgment',
      'Take one conscious action that reflects your chosen quality',
    ],
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    iconBg: 'bg-sky-100',
    emoji: '☀️',
  },
  {
    time: 'Midday',
    title: 'Humanity Checkpoint',
    duration: '2–3 min',
    description:
      'A brief but powerful pause to reconnect with your shared humanity and reset your emotional compass.',
    steps: [
      'Close your eyes and take one slow breath',
      'Ask: "Have I treated others — and myself — with dignity today?"',
      'Acknowledge one moment of connection or kindness you witnessed',
    ],
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    emoji: '🤝',
  },
  {
    time: 'Evening',
    title: 'Reflection + Forgiveness + Release',
    duration: '7–10 min',
    description:
      'Close your day with honest reflection, compassionate forgiveness, and a conscious release of what no longer serves you.',
    steps: [
      'Reflect: "What happened today that stirred emotion in me?"',
      'Acknowledge without judgment — name the feeling and its source',
      'Practice forgiveness: for others, and for yourself',
      "Write one sentence in your journal releasing the day's tension",
      'End with gratitude — name three things, however small',
    ],
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    iconBg: 'bg-violet-100',
    emoji: '🌙',
  },
];

/* ─────────────────────────────────────────────
   JOURNAL MODAL
───────────────────────────────────────────── */
interface JournalModalProps {
  title: string;
  promptText: string;
  onClose: () => void;
  onSave: (entry: string, name: string) => Promise<void>;
  saving: boolean;
  saved: boolean;
}

function JournalModal({ title, promptText, onClose, onSave, saving, saved }: JournalModalProps) {
  const [entry, setEntry] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Icon name="PencilSquareIcon" size={18} className="text-white" />
            </div>
            <h3 className="text-white font-headline font-bold text-base">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <Icon name="XMarkIcon" size={16} className="text-white" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Prompt */}
          <div className="bg-primary/8 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-foreground font-body italic leading-relaxed">{promptText}</p>
          </div>

          {/* Journal Entry */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Journal Entry <span className="text-red-500">*</span>
            </label>
            <textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              rows={5}
              placeholder="Write your honest reflection here…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Leave blank to submit as Anonymous"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>

          {saved && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Icon name="CheckCircleIcon" size={18} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-body font-medium">
                Your response has been saved! Keep going — you are doing great.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => onSave(entry, name)}
              disabled={saving || !entry.trim()}
              className="flex-1 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Icon name="CheckIcon" size={16} className="text-primary-foreground" />
                  Save Response
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-border text-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EAT Utilities (Day 1)
───────────────────────────────────────────── */
function getEATNow(): Date {
  const now = new Date();
  const eatOffsetMs = 3 * 60 * 60 * 1000;
  return new Date(now.getTime() + eatOffsetMs - now.getTimezoneOffset() * 60 * 1000);
}

function getEATDateString(date?: Date): string {
  const d = date || getEATNow();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDayElapsed(startDateStr: string): number {
  const todayStr = getEATDateString();
  const start = new Date(startDateStr + 'T00:00:00Z');
  const today = new Date(todayStr + 'T00:00:00Z');
  return Math.max(0, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const DailyExperienceSection = () => {
  const DAY_NUMBER = 1;
  const DAY_TITLE = 'Emotional Baseline';

  // Section open/close
  const [sectionOpen, setSectionOpen] = useState(false);

  // Tab: 'scenarios' | 'rituals'
  const [activeTab, setActiveTab] = useState<'scenarios' | 'rituals'>('scenarios');

  // Scenarios state
  const [openScenario, setOpenScenario] = useState<number | null>(null);
  const [openCustomScenario, setOpenCustomScenario] = useState<string | null>(null);
  const [customScenarios, setCustomScenarios] = useState<CustomScenario[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState({
    context: '',
    reflection: '',
    example: '',
    emotional_response: '',
    insight: '',
    action: '',
    submitted_by: '',
  });

  // Rituals state
  const [expandedRitual, setExpandedRitual] = useState<number | null>(null);

  // Journal modal state
  const [journalModal, setJournalModal] = useState<{
    open: boolean;
    title: string;
    promptText: string;
    responseType: 'scenario' | 'ritual';
    scenarioId?: number;
    scenarioContext?: string;
    ritualTime?: string;
  } | null>(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);

  // Completed items tracking
  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set());
  const [completedRituals, setCompletedRituals] = useState<Set<string>>(new Set());
  const [completedCustomScenarios, setCompletedCustomScenarios] = useState<Set<string>>(new Set());

  // Day status: 'today' | 'past' | 'unknown'
  const [dayStatus, setDayStatus] = useState<'today' | 'past' | 'unknown'>('unknown');

  // Refs for anchor scroll targets
  const scenariosRef = useRef<HTMLDivElement>(null);
  const ritualsRef = useRef<HTMLDivElement>(null);
  const scenarioRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const ritualRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (sectionOpen) loadCustomScenarios();
  }, [sectionOpen]);

  // Load journey start date to determine day status
  useEffect(() => {
    const loadDayStatus = async () => {
      try {
        const savedEmail = typeof window !== 'undefined' ? localStorage.getItem('calendar_email') : null;
        if (savedEmail) {
          const supabase = createClient();
          const { data } = await supabase
            .from('calendar_progress')
            .select('journey_start_date')
            .eq('email', savedEmail)
            .maybeSingle();

          if (data?.journey_start_date) {
            const elapsed = getDayElapsed(data.journey_start_date);
            // Day 1 is "today" when elapsed === 0, "past" when elapsed > 0
            setDayStatus(elapsed === 0 ? 'today' : 'past');
            return;
          }
        }
        // No journey started — Day 1 is today by default (it's always the first day)
        setDayStatus('today');
      } catch {
        setDayStatus('today');
      }
    };

    loadDayStatus();
  }, []);

  // Handle anchor hash navigation on mount / hash change
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (!hash) return;

      // Ritual anchors: #ritual-Morning, #ritual-Daytime, etc.
      const ritualMatch = hash.match(/^#ritual-(.+)$/);
      if (ritualMatch) {
        const ritualTime = decodeURIComponent(ritualMatch[1]);
        setSectionOpen(true);
        setActiveTab('rituals');
        const ritualIndex = rituals.findIndex((r) => r.time === ritualTime);
        if (ritualIndex !== -1) setExpandedRitual(ritualIndex);
        setTimeout(() => {
          ritualRefs.current[ritualTime]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        return;
      }

      // Scenario anchors: #scenario-1, #scenario-2, etc.
      const scenarioMatch = hash.match(/^#scenario-(\d+)$/);
      if (scenarioMatch) {
        const scenarioId = parseInt(scenarioMatch[1]);
        setSectionOpen(true);
        setActiveTab('scenarios');
        setOpenScenario(scenarioId);
        setTimeout(() => {
          scenarioRefs.current[scenarioId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350);
        return;
      }

      // Tab anchors: #activities, #rituals
      if (hash === '#activities') {
        setSectionOpen(true);
        setActiveTab('scenarios');
        setTimeout(() => scenariosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      } else if (hash === '#rituals') {
        setSectionOpen(true);
        setActiveTab('rituals');
        setTimeout(() => ritualsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
      }
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  const loadCustomScenarios = async () => {
    setLoadingScenarios(true);
    const data = await fetchCustomScenarios();
    setCustomScenarios(data);
    setLoadingScenarios(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const { data, error } = await insertCustomScenario({
      context: form.context.trim(),
      reflection: form.reflection.trim(),
      example: form.example.trim(),
      emotional_response: form.emotional_response.trim(),
      insight: form.insight.trim(),
      action: form.action.trim(),
      submitted_by: form.submitted_by.trim() || 'Anonymous',
    });
    setSubmitting(false);
    if (error) {
      setSubmitError(error);
    } else if (data) {
      setCustomScenarios((prev) => [data, ...prev]);
      setSubmitSuccess(true);
      setShowAddForm(false);
      setForm({ context: '', reflection: '', example: '', emotional_response: '', insight: '', action: '', submitted_by: '' });
      setTimeout(() => setSubmitSuccess(false), 4000);
    }
  };

  const openJournalForScenario = (scenario: Scenario) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `Day ${DAY_NUMBER} · ${scenario.context}`,
      promptText: scenario.action,
      responseType: 'scenario',
      scenarioId: scenario.id,
      scenarioContext: scenario.context,
    });
  };

  const openJournalForCustomScenario = (cs: CustomScenario) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `Community Scenario · ${cs.context}`,
      promptText: cs.action,
      responseType: 'scenario',
      scenarioId: undefined,
      scenarioContext: `custom:${cs.id}`,
    });
  };

  const openJournalForRitual = (ritual: Ritual) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `${ritual.emoji} ${ritual.time} Ritual · ${ritual.title}`,
      promptText: `Reflect on your ${ritual.time.toLowerCase()} practice: ${ritual.description}`,
      responseType: 'ritual',
      ritualTime: ritual.time,
    });
  };

  const handleJournalSave = async (entry: string, name: string) => {
    if (!journalModal || !entry.trim()) return;
    setJournalSaving(true);
    const isCustom = journalModal.scenarioContext?.startsWith('custom:');
    const customId = isCustom ? journalModal.scenarioContext!.replace('custom:', '') : null;
    const { error } = await saveDailyResponse({
      day_number: DAY_NUMBER,
      day_title: DAY_TITLE,
      response_type: journalModal.responseType,
      scenario_id: journalModal.scenarioId ?? null,
      scenario_context: isCustom ? journalModal.scenarioContext!.replace('custom:', '') : (journalModal.scenarioContext ?? null),
      ritual_time: journalModal.ritualTime ?? null,
      journal_entry: entry.trim(),
      completed: true,
      participant_name: name.trim() || 'Anonymous',
    });
    setJournalSaving(false);
    if (!error) {
      setJournalSaved(true);
      if (isCustom && customId) {
        setCompletedCustomScenarios((prev) => new Set(prev).add(customId));
      } else if (journalModal.responseType === 'scenario' && journalModal.scenarioId) {
        setCompletedScenarios((prev) => new Set(prev).add(journalModal.scenarioId!));
      } else if (journalModal.responseType === 'ritual' && journalModal.ritualTime) {
        setCompletedRituals((prev) => new Set(prev).add(journalModal.ritualTime!));
      }
      setTimeout(() => setJournalModal(null), 2000);
    }
  };

  const totalCompleted = completedScenarios.size + completedRituals.size;
  const totalItems = scenarios.length + rituals.length;

  return (
    <section className="py-16 lg:py-24 bg-background" id="daily-experience">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* ── Clickable Section Header ── */}
        <button
          onClick={() => setSectionOpen(!sectionOpen)}
          className="w-full text-left group focus:outline-none"
          aria-expanded={sectionOpen}
        >
          <div className={`rounded-2xl border-2 transition-all duration-300 p-8 ${sectionOpen ? 'border-primary bg-primary/5' : 'border-primary/30 bg-gradient-to-br from-primary/8 to-secondary/8 hover:border-primary hover:bg-primary/10'}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-5">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${sectionOpen ? 'bg-primary' : 'bg-primary/20 group-hover:bg-primary'}`}>
                  <Icon name="SparklesIcon" size={30} className={`transition-colors duration-300 ${sectionOpen ? 'text-white' : 'text-primary group-hover:text-white'}`} />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className="text-xs font-cta font-bold text-primary uppercase tracking-widest">Week 1 · Self-Awareness</span>
                    {dayStatus === 'today' && (
                      <span className="text-xs font-cta font-bold text-white bg-primary rounded-full px-2 py-0.5">Today</span>
                    )}
                    {dayStatus === 'past' && (
                      <span className="text-xs font-cta font-bold text-white bg-green-600 rounded-full px-2 py-0.5">✓ Completed</span>
                    )}
                  </div>
                  <h2 className="text-2xl lg:text-4xl font-headline font-bold text-primary leading-tight">
                    Day 1: Emotional Baseline
                  </h2>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    5 life scenarios · 4 daily rituals · Journaling actions · ~45 min total
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {totalCompleted > 0 && (
                  <span className="text-xs font-cta font-bold text-green-600 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                    {totalCompleted}/{totalItems} completed
                  </span>
                )}
                <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${sectionOpen ? 'bg-primary border-primary' : 'border-primary/40 group-hover:border-primary group-hover:bg-primary/10'}`}>
                  <Icon
                    name={sectionOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={22}
                    className={`transition-colors duration-300 ${sectionOpen ? 'text-white' : 'text-primary'}`}
                  />
                </div>
              </div>
            </div>

            {!sectionOpen && (
              <div className="mt-5 flex flex-wrap gap-3 items-center">
                <span className="text-sm text-muted-foreground font-body">Includes:</span>
                {['Teamwork', 'Love Affairs', 'Marriage', 'Workplace', 'Unemployment'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-cta font-bold border border-primary/20">
                    {tag}
                  </span>
                ))}
                {['🌅 Morning', '☀️ Daytime', '🤝 Midday', '🌙 Evening'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-cta font-bold border border-amber-200">
                    {tag}
                  </span>
                ))}
                <span className="ml-auto text-sm font-cta font-bold text-primary flex items-center gap-1">
                  <Icon name="HandPointerIcon" size={16} className="text-primary" />
                  Click to explore
                </span>
              </div>
            )}
          </div>
        </button>

        {/* ── Expandable Content ── */}
        {sectionOpen && (
          <div className="mt-8 animate-in fade-in slide-in-from-top-2 duration-300">

            {/* Objective */}
            <div className="text-center mb-8">
              <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
                <strong>Objective:</strong> Identify your dominant emotions and their immediate triggers within different life situations. This is the first step toward forgiveness, healing, unity, and living in peace.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {['Forgive', 'Heal', 'Unity', 'Live in Peace'].map((tag) => (
                  <span key={tag} className="px-4 py-1 bg-accent/20 text-accent-foreground rounded-full text-sm font-cta font-bold">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            {totalCompleted > 0 && (
              <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-cta font-bold text-green-700">Today's Progress</span>
                  <span className="text-sm font-cta font-bold text-green-700">{totalCompleted} / {totalItems} activities</span>
                </div>
                <div className="w-full bg-green-100 rounded-full h-2.5">
                  <div
                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${(totalCompleted / totalItems) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* ── Tab Navigation ── */}
            <div className="flex gap-2 mb-8 bg-muted/40 rounded-2xl p-1.5 border border-border">
              <button
                id="activities"
                onClick={() => setActiveTab('scenarios')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-cta text-sm font-bold transition-all duration-200 ${
                  activeTab === 'scenarios' ?'bg-white text-primary shadow-sm border border-primary/20' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="BookOpenIcon" size={18} className={activeTab === 'scenarios' ? 'text-primary' : 'text-muted-foreground'} />
                <span>Daily Activities</span>
                <span className={`text-xs rounded-full px-2 py-0.5 font-bold ${activeTab === 'scenarios' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {completedScenarios.size}/{scenarios.length}
                </span>
              </button>
              <button
                id="rituals"
                onClick={() => setActiveTab('rituals')}
                className={`flex-1 flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl font-cta text-sm font-bold transition-all duration-200 ${
                  activeTab === 'rituals' ?'bg-white text-amber-700 shadow-sm border border-amber-200' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="text-base">✨</span>
                <span>Daily Ritual Practices</span>
                <span className={`text-xs rounded-full px-2 py-0.5 font-bold ${activeTab === 'rituals' ? 'bg-amber-50 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
                  {completedRituals.size}/{rituals.length}
                </span>
              </button>
            </div>

            {/* ══════════════════════════════════════
                TAB: DAILY ACTIVITIES (SCENARIOS)
            ══════════════════════════════════════ */}
            {activeTab === 'scenarios' && (
              <div ref={scenariosRef}>
                {/* How to Use */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 mb-8 border border-primary/20">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="LightBulbIcon" size={24} className="text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-bold text-primary mb-2">How to Use This Guide</h3>
                      <p className="text-sm text-foreground font-body leading-relaxed">
                        For each life scenario below, read the reflection questions, consider the example, and identify your own emotional response. Then click <strong>"Record My Response"</strong> to write your personal journal entry — your response will be saved for today.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Scenarios */}
                <div className="space-y-5">
                  {scenarios.map((scenario) => {
                    const isDone = completedScenarios.has(scenario.id);
                    return (
                      <div
                        key={scenario.id}
                        id={`scenario-${scenario.id}`}
                        ref={(el) => { scenarioRefs.current[scenario.id] = el; }}
                        className={`bg-card rounded-xl shadow-md border overflow-hidden transition-all duration-200 ${isDone ? 'border-green-300 bg-green-50/30' : 'border-border'}`}
                      >
                        {/* Scenario Header */}
                        <button
                          onClick={() => setOpenScenario(openScenario === scenario.id ? null : scenario.id)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors duration-200"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-green-100' : 'bg-primary/10'}`}>
                              {isDone ? (
                                <Icon name="CheckCircleIcon" size={24} className="text-green-600" />
                              ) : (
                                <Icon name={scenario.icon as any} size={24} className="text-primary" />
                              )}
                            </div>
                            <div>
                              <span className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wide">
                                Scenario {scenario.id} {isDone && '· ✓ Completed'}
                              </span>
                              <h3 className="text-lg lg:text-xl font-headline font-bold text-primary">
                                {scenario.context}
                              </h3>
                            </div>
                          </div>
                          <Icon
                            name={openScenario === scenario.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                            size={22}
                            className="text-muted-foreground flex-shrink-0"
                          />
                        </button>

                        {/* Scenario Body */}
                        {openScenario === scenario.id && (
                          <div className="px-6 pb-8 border-t border-border">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="QuestionMarkCircleIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Reflection Questions</h4>
                                </div>
                                <ul className="space-y-2">
                                  {scenario.reflection.map((q, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-foreground font-body">
                                      <span className="text-primary font-bold mt-0.5">•</span>
                                      <span>{q}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="DocumentTextIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Example Scenario</h4>
                                </div>
                                <p className="text-sm text-foreground font-body italic">"{scenario.example}"</p>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="FaceSmileIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Emotional Response</h4>
                                </div>
                                <p className="text-sm text-foreground font-body">{scenario.emotionalResponse}</p>
                              </div>
                              <div className="bg-muted/40 rounded-lg p-5">
                                <div className="flex items-center gap-2 mb-3">
                                  <Icon name="EyeIcon" size={20} className="text-secondary" />
                                  <h4 className="text-sm font-cta font-bold text-secondary uppercase tracking-wide">Expected Insight</h4>
                                </div>
                                <p className="text-sm text-foreground font-body">{scenario.insight}</p>
                              </div>
                            </div>

                            {/* Practical Action + Record Button */}
                            <div className="mt-6 bg-primary/10 border border-primary/30 rounded-lg p-5">
                              <div className="flex items-center gap-2 mb-3">
                                <Icon name="PencilSquareIcon" size={20} className="text-primary" />
                                <h4 className="text-sm font-cta font-bold text-primary uppercase tracking-wide">Practical Action — Write in Your Journal</h4>
                              </div>
                              <p className="text-base text-foreground font-body italic font-medium mb-4">
                                {scenario.action}
                              </p>
                              <button
                                onClick={() => openJournalForScenario(scenario)}
                                className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md ${
                                  isDone
                                    ? 'bg-green-100 text-green-700 border border-green-300' :'bg-primary text-primary-foreground'
                                }`}
                              >
                                <Icon name={isDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isDone ? 'text-green-600' : 'text-primary-foreground'} />
                                {isDone ? 'Response Saved ✓' : 'Record My Response'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Community Scenarios */}
                  {customScenarios.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-widest px-2">
                          Community Scenarios ({customScenarios.length})
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                      {loadingScenarios ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="ml-3 text-sm text-muted-foreground font-body">Loading…</span>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {customScenarios.map((cs) => {
                            const isCustomDone = completedCustomScenarios.has(cs.id);
                            return (
                            <div key={cs.id} className="bg-card rounded-xl shadow-sm border border-secondary/30 overflow-hidden">
                              <button
                                onClick={() => setOpenCustomScenario(openCustomScenario === cs.id ? null : cs.id)}
                                className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/20 transition-colors duration-200"
                              >
                                <div className="flex items-center gap-4">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCustomDone ? 'bg-green-100' : 'bg-secondary/15'}`}>
                                    {isCustomDone ? (
                                      <Icon name="CheckCircleIcon" size={22} className="text-green-600" />
                                    ) : (
                                      <Icon name="UserCircleIcon" size={22} className="text-secondary" />
                                    )}
                                  </div>
                                  <div>
                                    <span className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">
                                      Community · {cs.submitted_by || 'Anonymous'}{isCustomDone && ' · ✓ Completed'}
                                    </span>
                                    <h3 className="text-base lg:text-lg font-headline font-bold text-foreground">{cs.context}</h3>
                                  </div>
                                </div>
                                <Icon name={openCustomScenario === cs.id ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={20} className="text-muted-foreground flex-shrink-0" />
                              </button>
                              {openCustomScenario === cs.id && (
                                <div className="px-5 pb-6 border-t border-border">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
                                    <div className="bg-muted/40 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2"><Icon name="QuestionMarkCircleIcon" size={18} className="text-secondary" /><h4 className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">Reflection</h4></div>
                                      <p className="text-sm text-foreground font-body">{cs.reflection}</p>
                                    </div>
                                    <div className="bg-muted/40 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2"><Icon name="DocumentTextIcon" size={18} className="text-secondary" /><h4 className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">Example</h4></div>
                                      <p className="text-sm text-foreground font-body italic">"{cs.example}"</p>
                                    </div>
                                    <div className="bg-muted/40 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2"><Icon name="FaceSmileIcon" size={18} className="text-secondary" /><h4 className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">Emotional Response</h4></div>
                                      <p className="text-sm text-foreground font-body">{cs.emotional_response}</p>
                                    </div>
                                    <div className="bg-muted/40 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2"><Icon name="EyeIcon" size={18} className="text-secondary" /><h4 className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">Insight</h4></div>
                                      <p className="text-sm text-foreground font-body">{cs.insight}</p>
                                    </div>
                                  </div>
                                  <div className="mt-4 bg-secondary/10 border border-secondary/30 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-2"><Icon name="PencilSquareIcon" size={18} className="text-secondary" /><h4 className="text-xs font-cta font-bold text-secondary uppercase tracking-wide">Journal Action</h4></div>
                                    <p className="text-sm text-foreground font-body italic">{cs.action}</p>
                                  </div>
                                  <div className="mt-4">
                                    <button
                                      onClick={() => openJournalForCustomScenario(cs)}
                                      className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md ${
                                        isCustomDone
                                          ? 'bg-green-100 text-green-700 border border-green-300' :'bg-primary text-primary-foreground'
                                      }`}
                                    >
                                      <Icon name={isCustomDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isCustomDone ? 'text-green-600' : 'text-primary-foreground'} />
                                      {isCustomDone ? 'Response Saved ✓' : 'Record My Response'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add Your Scenario */}
                  <div className="mt-10">
                    <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-2xl border-2 border-secondary/30 p-8">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="PlusCircleIcon" size={26} className="text-secondary" />
                          </div>
                          <div>
                            <h3 className="text-xl font-headline font-bold text-primary">Add Your Own Scenario</h3>
                            <p className="text-sm text-muted-foreground font-body">Share a life situation you face — it will be saved for the community.</p>
                          </div>
                        </div>
                        {!showAddForm && (
                          <button onClick={() => setShowAddForm(true)} className="flex-shrink-0 px-6 py-2.5 bg-secondary text-secondary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md">
                            + Add Scenario
                          </button>
                        )}
                      </div>
                      {submitSuccess && (
                        <div className="mt-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0" />
                          <p className="text-sm text-green-700 font-body font-medium">Your scenario has been submitted!</p>
                        </div>
                      )}
                      {showAddForm && (
                        <form onSubmit={handleAddScenario} className="mt-6 space-y-5">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            <div className="lg:col-span-2">
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Scenario Title / Context <span className="text-red-500">*</span></label>
                              <input type="text" name="context" value={form.context} onChange={handleFormChange} required placeholder="e.g. Sibling Rivalry, Financial Stress, Grief…" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition" />
                            </div>
                            <div>
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Reflection Question <span className="text-red-500">*</span></label>
                              <textarea name="reflection" value={form.reflection} onChange={handleFormChange} required rows={3} placeholder="What emotion do you feel in this situation?" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Example Situation <span className="text-red-500">*</span></label>
                              <textarea name="example" value={form.example} onChange={handleFormChange} required rows={3} placeholder="Describe a specific moment or event…" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Emotional Response <span className="text-red-500">*</span></label>
                              <input type="text" name="emotional_response" value={form.emotional_response} onChange={handleFormChange} required placeholder="e.g. Anger, sadness, jealousy…" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition" />
                            </div>
                            <div>
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Your Insight <span className="text-red-500">*</span></label>
                              <textarea name="insight" value={form.insight} onChange={handleFormChange} required rows={3} placeholder="What did you learn about yourself from this emotion?" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Journal Action <span className="text-red-500">*</span></label>
                              <textarea name="action" value={form.action} onChange={handleFormChange} required rows={3} placeholder='"I feel… because I value…"' className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div className="lg:col-span-2">
                              <label className="block text-sm font-cta font-bold text-foreground mb-1.5">Your Name <span className="text-muted-foreground font-normal">(optional)</span></label>
                              <input type="text" name="submitted_by" value={form.submitted_by} onChange={handleFormChange} placeholder="Leave blank to submit as Anonymous" className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition" />
                            </div>
                          </div>
                          {submitError && (
                            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                              <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 flex-shrink-0" />
                              <p className="text-sm text-red-600 font-body">{submitError}</p>
                            </div>
                          )}
                          <div className="flex gap-3 pt-1">
                            <button type="submit" disabled={submitting} className="px-8 py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
                              {submitting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</> : <><Icon name="CheckIcon" size={16} className="text-primary-foreground" />Submit Scenario</>}
                            </button>
                            <button type="button" onClick={() => { setShowAddForm(false); setSubmitError(null); }} className="px-6 py-2.5 border border-border text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-muted">Cancel</button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ══════════════════════════════════════
                TAB: DAILY RITUAL PRACTICES
            ══════════════════════════════════════ */}
            {activeTab === 'rituals' && (
              <div ref={ritualsRef}>
                {/* Intro */}
                <div className="bg-gradient-to-br from-amber-50 to-violet-50 border border-amber-200 rounded-xl p-6 mb-8">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center text-xl">✨</div>
                    <div>
                      <h3 className="text-lg font-headline font-bold text-slate-800 mb-2">Your Daily Ritual Practices</h3>
                      <p className="text-sm text-slate-600 font-body leading-relaxed">
                        Four intentional pauses woven into your day — each one a doorway to greater awareness, compassion, and inner peace. Choose any ritual, expand it to read the steps, then click <strong>"Record My Practice"</strong> to log your reflection.
                      </p>
                      <p className="text-xs text-slate-500 font-body mt-2">
                        <span className="font-semibold">Total daily commitment:</span>{' '}
                        <span className="text-indigo-600 font-medium">~20–27 minutes</span>
                        {' '}— less time than a coffee break, more impact than you can imagine.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Ritual Cards */}
                <div className="space-y-4">
                  {rituals.map((ritual, index) => {
                    const isDone = completedRituals.has(ritual.time);
                    const isExpanded = expandedRitual === index;
                    return (
                      <div
                        key={index}
                        id={`ritual-${ritual.time}`}
                        ref={(el) => { ritualRefs.current[ritual.time] = el; }}
                        className={`rounded-2xl border-2 overflow-hidden transition-all duration-200 ${isDone ? 'border-green-300 bg-green-50/40' : `${ritual.borderColor} ${ritual.bgColor}`}`}
                      >
                        {/* Ritual Header */}
                        <button
                          onClick={() => setExpandedRitual(isExpanded ? null : index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:opacity-90 transition-opacity duration-200"
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl ${isDone ? 'bg-green-100' : ritual.iconBg}`}>
                              {isDone ? '✅' : ritual.emoji}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-semibold uppercase tracking-wider ${isDone ? 'text-green-700' : ritual.color}`}>
                                  {ritual.time} {isDone && '· ✓ Completed'}
                                </span>
                                <span className={`text-xs rounded-full px-2 py-0.5 border ${isDone ? 'bg-green-50 text-green-700 border-green-200' : `${ritual.iconBg} ${ritual.color} ${ritual.borderColor}`}`}>
                                  {ritual.duration}
                                </span>
                              </div>
                              <h3 className="text-base font-bold text-slate-800 mt-0.5">{ritual.title}</h3>
                            </div>
                          </div>
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${ritual.iconBg}`}>
                            <svg className={`w-4 h-4 ${ritual.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>

                        {/* Ritual Body */}
                        {isExpanded && (
                          <div className="px-5 pb-6 border-t border-white/60">
                            <p className="text-slate-600 text-sm mt-4 leading-relaxed mb-4">{ritual.description}</p>
                            <p className={`text-xs font-semibold uppercase tracking-wider ${ritual.color} mb-3`}>How to practice:</p>
                            <div className="space-y-2 mb-6">
                              {ritual.steps.map((step, i) => (
                                <div key={i} className="flex items-start gap-2.5">
                                  <div className={`flex-shrink-0 w-5 h-5 rounded-full ${ritual.iconBg} border ${ritual.borderColor} flex items-center justify-center mt-0.5`}>
                                    <span className={`text-xs font-bold ${ritual.color}`}>{i + 1}</span>
                                  </div>
                                  <p className="text-slate-700 text-sm leading-relaxed">{step}</p>
                                </div>
                              ))}
                            </div>
                            <button
                              onClick={() => openJournalForRitual(ritual)}
                              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md ${
                                isDone
                                  ? 'bg-green-100 text-green-700 border border-green-300'
                                  : `${ritual.iconBg} ${ritual.color} border ${ritual.borderColor}`
                              }`}
                            >
                              <Icon name={isDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isDone ? 'text-green-600' : ritual.color} />
                              {isDone ? 'Practice Recorded ✓' : 'Record My Practice'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Closing CTA ── */}
            <div className="mt-14 bg-gradient-to-br from-primary to-secondary rounded-xl p-8 text-center text-white">
              <Icon name="StarIcon" size={40} className="text-white/80 mx-auto mb-4" />
              <h3 className="text-2xl font-headline font-bold mb-3">Complete Your Day 1 Practice</h3>
              <p className="text-base font-body text-white/90 max-w-2xl mx-auto mb-6">
                Take 20 minutes today to journal your emotional baseline across these five life areas and complete your daily rituals. This self-awareness is the foundation of your 30-day transformation journey toward living with no enemy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/festival-guide#calendar" className="px-8 py-3 bg-white text-primary rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg">
                  Continue to Day 2 →
                </a>
                <a href="/journal-history" className="px-8 py-3 bg-white/20 text-white border border-white/40 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-white/30">
                  View Journal History
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Journal Modal ── */}
      {journalModal?.open && (
        <JournalModal
          title={journalModal.title}
          promptText={journalModal.promptText}
          onClose={() => setJournalModal(null)}
          onSave={handleJournalSave}
          saving={journalSaving}
          saved={journalSaved}
        />
      )}
    </section>
  );
};

export default DailyExperienceSection;
